import React, { Suspense } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { SettingsProvider } from "@/lib/useSettings";
import { UniverseProvider } from "@/lib/useUniverse";
import { CartProvider } from "@/lib/useCart";
import { CustomerAuthProvider } from "@/lib/useCustomerAuth";
import FeatureGate from "@/components/FeatureGate";

import PublicLayout from "@/components/public/PublicLayout";
import HomePage from "@/pages/public/HomePage";
import { ServicesListPage, ServiceDetailPage } from "@/pages/public/ServicesPage";
import { RealisationsListPage, RealisationDetailPage } from "@/pages/public/RealisationsPage";
import { BlogListPage, BlogDetailPage } from "@/pages/public/BlogPage";
import ZonePage from "@/pages/public/ZonePage";
import { ContactPage, DevisPage, LegalPage, DeliveryReturnsPage } from "@/pages/public/MiscPages";
import DynamicQuoteFormPage from "@/pages/public/DynamicQuoteFormPage";
import LocalPage from "@/pages/public/LocalPage";

import QuotePublicPage from "@/pages/public/QuotePublicPage";
import CustomerProjectsPageHub, { CustomerProjectDetailPage as CustomerProjectDetailPageHub } from "@/pages/customer/CustomerProjectsPage";
import TeamPage from "@/pages/public/TeamPage";
import ShopPage from "@/pages/public/ShopPage";
import ProductPage from "@/pages/public/ProductPage";
import ReservationPage from "@/pages/public/ReservationPage";
import BookingConfirmationPage from "@/pages/public/BookingConfirmationPage";
import { RentalsListPage, RentalDetailPage, RentalConfirmationPage } from "@/pages/public/RentalsPage";
import BrandPreviewPage from "@/pages/BrandPreviewPage";
import OnboardingRequestPage from "@/pages/public/OnboardingRequestPage";
import RealisationCategoryPage from "@/pages/public/RealisationCategoryPage";
import BlogCategoryPage from "@/pages/public/BlogCategoryPage";
import OnboardingConfirmationPage from "@/pages/public/OnboardingConfirmationPage";
import IphOnboardingPage from "@/pages/public/IphOnboardingPage";
import {
  CustomerLoginPage, CustomerRegisterPage, AccountLayout,
  AccountDashboard, AccountOrders, AccountOrderDetail, AccountAddresses, AccountProfile, AccountFavorites, AccountWallet,
  OrderConfirmationPage,
} from "@/pages/public/AccountPages";
import ShortLinkRedirect from "@/pages/ShortLinkRedirect";
import RootRouter from "@/pages/RootRouter";
import ScrollToTop from "@/components/ScrollToTop";
import CockpitPortfolioPage from "@/pages/CockpitPortfolioPage";
import CockpitFeaturesPage from "@/pages/CockpitFeaturesPage";
import PublicValidatePage from "@/pages/public/PublicValidatePage";
import { SportHomePage, SportPlayersPage, SportCalendarPage, SportClubPage, SportPublicLayout } from "@/pages/public/SportClub";
import SportBoutique from "@/pages/public/SportBoutique";
import SportCheckout from "@/pages/public/SportCheckout";
import CoachPlanningPage from "@/pages/public/CoachPlanningPage";
import SportMedias from "@/pages/public/SportMedias";
import SportHistoire from "@/pages/public/SportHistoire";
import SportPartenaires from "@/pages/public/SportPartenaires";
import SportSupporters from "@/pages/public/SportSupporters";
import SportRejoindre from "@/pages/public/SportRejoindre";
import SportClubInnerPage from "@/pages/public/SportClubInnerPage";import SportLegalPage from "@/pages/public/SportLegalPage";
import SportClassementPage from "@/pages/public/SportClassementPage";
import SportPlayerDetail from "@/pages/public/SportPlayerDetail";
import SportMonEspace from "@/pages/public/SportMonEspace";
import SportPerfsWall from "@/pages/public/SportPerfsWall";
import SportOperator from "@/pages/public/SportOperator";
import SportSpeaker from "@/pages/public/SportSpeaker";
import SportGamesAdmin from "@/pages/public/SportGamesAdmin";
import SportProduct from "@/pages/public/SportProduct";
import SportActualites from "@/pages/public/SportActualites";
import SportArticleDetail from "@/pages/public/SportArticleDetail";
import EmbedWidgets from "@/pages/public/EmbedWidgets";

// === LAZY LOADED ADMIN/AGENCY/CUSTOMER PAGES (perf) ===
const LoginPage = React.lazy(() => import("@/pages/admin/LoginPage"));
const AdminForgotPasswordPage = React.lazy(() => import("@/pages/admin/AdminForgotPasswordPage"));
const AdminResetPasswordPage = React.lazy(() => import("@/pages/admin/AdminResetPasswordPage"));
const AdminLayout = React.lazy(() => import("@/pages/admin/AdminLayout"));
const Dashboard = React.lazy(() => import("@/pages/admin/Dashboard"));
const ServicesAdmin = React.lazy(() => import("@/pages/admin/ServicesAdmin"));
const RealisationsAdmin = React.lazy(() => import("@/pages/admin/RealisationsAdmin"));
const QuoteFormsAdmin = React.lazy(() => import("@/pages/admin/QuoteFormsAdmin"));
const QuoteRequestsAdmin = React.lazy(() => import("@/pages/admin/QuoteRequestsAdmin"));
const QuoteEditorPage = React.lazy(() => import("@/pages/admin/QuoteEditorPage"));
const PodSsoClientConsumePage = React.lazy(() => import("@/pages/admin/PodSsoClientConsumePage"));
const CustomerLayoutHub = React.lazy(() => import("@/pages/customer/CustomerLayout"));
const CustomerLoginPageHub = React.lazy(() => import("@/pages/customer/CustomerLoginPage"));
const CustomerSetPasswordPageHub = React.lazy(() => import("@/pages/customer/CustomerSetPasswordPage"));
const CustomerDashboardPageHub = React.lazy(() => import("@/pages/customer/CustomerDashboardPage"));
const CustomerSiteDetailsPageHub = React.lazy(() => import("@/pages/customer/CustomerSiteDetailsPage"));
const CustomerQuotesListPageHub = React.lazy(() => import("@/pages/customer/CustomerQuotesListPage"));
const CustomerQuoteNewPageHub = React.lazy(() => import("@/pages/customer/CustomerQuoteNewPage"));
const CustomerQuoteDetailPageHub = React.lazy(() => import("@/pages/customer/CustomerQuoteDetailPage"));
const CustomerMagicLoginPageHub = React.lazy(() => import("@/pages/customer/CustomerMagicLoginPage"));
const CustomerContactsPageHub = React.lazy(() => import("@/pages/customer/CustomerContactsPage"));
const ProjectsKanbanAdmin = React.lazy(() => import("@/pages/agency/ProjectsKanbanAdmin"));
const HubCustomersAdmin = React.lazy(() => import("@/pages/agency/HubCustomersAdmin"));
const BlogAdmin = React.lazy(() => import("@/pages/admin/BlogAdmin"));
const FAQAdmin = React.lazy(() => import("@/pages/admin/SimpleAdminPages").then(m => ({ default: m.FAQAdmin })));
const ZonesAdmin = React.lazy(() => import("@/pages/admin/SimpleAdminPages").then(m => ({ default: m.ZonesAdmin })));
const LeadsAdmin = React.lazy(() => import("@/pages/admin/LeadsAdmin"));
const RankTrackerAdmin = React.lazy(() => import("@/pages/admin/AdvancedAdminPages").then(m => ({ default: m.RankTrackerAdmin })));
const AnalyticsAdmin = React.lazy(() => import("@/pages/admin/AdvancedAdminPages").then(m => ({ default: m.AnalyticsAdmin })));
const LocalSEOAdmin = React.lazy(() => import("@/pages/admin/AdvancedAdminPages").then(m => ({ default: m.LocalSEOAdmin })));
const SettingsAdmin = React.lazy(() => import("@/pages/admin/SettingsAdmin"));
const IdentityAdmin = React.lazy(() => import("@/pages/admin/IdentityAdmin"));
const RolesAdmin = React.lazy(() => import("@/pages/admin/RolesAdmin"));
const AmateurTeamsAdmin = React.lazy(() => import("@/pages/admin/AmateurTeamsAdmin"));
const SportArticlesAdmin = React.lazy(() => import("@/pages/admin/SportArticlesAdmin"));
const SportProductsAdmin = React.lazy(() => import("@/pages/admin/SportProductsAdmin"));
const SportPagesAdmin = React.lazy(() => import("@/pages/admin/SportPagesAdmin"));
const SupportTicketsAdmin = React.lazy(() => import("@/pages/admin/SupportTicketsAdmin"));
const PosAdmin = React.lazy(() => import("@/pages/admin/PosAdmin"));
const PosCashier = React.lazy(() => import("@/pages/admin/PosCashier"));
const PosSessions = React.lazy(() => import("@/pages/admin/PosSessions").then(m => ({ default: m.PosSessions })));
const PosZView = React.lazy(() => import("@/pages/admin/PosSessions").then(m => ({ default: m.PosZView })));
const MediaAdmin = React.lazy(() => import("@/pages/admin/MediaAdmin"));
const ShortLinksAdmin = React.lazy(() => import("@/pages/admin/ShortLinksAdmin"));
const ThemesAdmin = React.lazy(() => import("@/pages/admin/ThemesAdmin"));
const ProductsAdmin = React.lazy(() => import("@/pages/admin/ProductsAdmin"));
const PickupAdmin = React.lazy(() => import("@/pages/admin/PickupAdmin"));
const OrdersAdmin = React.lazy(() => import("@/pages/admin/OrdersAdmin"));
const HeaderAdmin = React.lazy(() => import("@/pages/admin/HeaderAdmin"));
const QrLabelsAdmin = React.lazy(() => import("@/pages/admin/QrLabelsAdmin"));
const ShopDashboard = React.lazy(() => import("@/pages/admin/ShopDashboard"));
const TeamAdmin = React.lazy(() => import("@/pages/admin/TeamAdmin"));
const BookingsAdmin = React.lazy(() => import("@/pages/admin/BookingsAdmin"));
const BookingsConfigAdmin = React.lazy(() => import("@/pages/admin/BookingsConfigAdmin"));
const WidgetsConsoleAdmin = React.lazy(() => import("@/pages/admin/WidgetsConsoleAdmin"));
const TechnicalAdmin = React.lazy(() => import("@/pages/admin/TechnicalAdmin"));
const TestModeAdmin = React.lazy(() => import("@/pages/admin/TestModeAdmin"));
const CategoriesAdmin = React.lazy(() => import("@/pages/admin/CategoriesAdmin"));
const CustomerGroupsAdmin = React.lazy(() => import("@/pages/admin/CustomerGroupsAdmin"));
const LicenseAdmin = React.lazy(() => import("@/pages/admin/LicenseAdmin"));
const UniversesAdmin = React.lazy(() => import("@/pages/admin/UniversesAdmin"));
const LegalConfigAdmin = React.lazy(() => import("@/pages/admin/LegalConfigAdmin"));
const LegalDocsAdmin = React.lazy(() => import("@/pages/admin/LegalDocsAdmin"));
const BillingAdmin = React.lazy(() => import("@/pages/admin/BillingAdmin"));
const EmailsAdmin = React.lazy(() => import("@/pages/admin/EmailsAdmin"));
const ShippingAdmin = React.lazy(() => import("@/pages/admin/ShippingAdmin"));
const BusinessRulesAdmin = React.lazy(() => import("@/pages/admin/BusinessRulesAdmin"));
const PopupCallbackAdmin = React.lazy(() => import("@/pages/admin/PopupCallbackAdmin"));
const WishlistAdmin = React.lazy(() => import("@/pages/admin/WishlistAdmin"));
const PaymentsAdmin = React.lazy(() => import("@/pages/admin/PaymentsAdmin"));
const OnboardingPage = React.lazy(() => import("@/pages/admin/OnboardingPage"));
const CockpitAdmin = React.lazy(() => import("@/pages/admin/CockpitAdmin"));
const StudioAdmin = React.lazy(() => import("@/pages/admin/StudioAdmin"));
const StaffAdmin = React.lazy(() => import("@/pages/admin/StaffAdmin"));
const HomeSectionsToggleAdmin = React.lazy(() => import("@/pages/admin/HomeSectionsToggleAdmin"));
const AgencyPairingPage = React.lazy(() => import("@/pages/admin/AgencyPairingPage"));
const SecurityAdmin = React.lazy(() => import("@/pages/admin/SecurityAdmin"));
const ProductsImporterAdmin = React.lazy(() => import("@/pages/admin/ProductsImporterAdmin"));
const RentalsAdmin = React.lazy(() => import("@/pages/admin/RentalsAdmin"));
const RentalsCalendarAdmin = React.lazy(() => import("@/pages/admin/RentalsCalendarAdmin"));
const RentalsDashboardAdmin = React.lazy(() => import("@/pages/admin/RentalsDashboardAdmin"));
const SectorPresetsAdmin = React.lazy(() => import("@/pages/admin/SectorPresetsAdmin"));
const AgencySitesDashboard = React.lazy(() => import("@/pages/admin/AgencySitesDashboard"));
const AgencyAlertsPage = React.lazy(() => import("@/pages/admin/AgencyAlertsPage"));
const AgencyLayout = React.lazy(() => import("@/pages/agency/AgencyLayout"));
const AgencyLoginPage = React.lazy(() => import("@/pages/agency/AgencyLoginPage"));
const AgencySettingsPage = React.lazy(() => import("@/pages/agency/AgencySettingsPage"));
const ThemeGeneratorAdvanced = React.lazy(() => import("@/pages/agency/ThemeGeneratorAdvanced"));
const ContentScraperPage = React.lazy(() => import("@/pages/agency/ContentScraperPage"));
const OnboardingDraftsPage = React.lazy(() => import("@/pages/agency/OnboardingDraftsPage"));
const AgencyCockpitAdmin = React.lazy(() => import("@/pages/agency/AgencyCockpitAdmin"));
const PromoCodesPage = React.lazy(() => import("@/pages/agency/PromoCodesPage"));
const DomainsPage = React.lazy(() => import("@/pages/agency/DomainsPage"));
const CockpitUpdatePage = React.lazy(() => import("@/pages/agency/CockpitUpdatePage"));
const PodsVaultPage = React.lazy(() => import("@/pages/agency/PodsVaultPage"));
const PodPairingWizardPage = React.lazy(() => import("@/pages/agency/PodPairingWizardPage"));
const AgentBridgePage = React.lazy(() => import("@/pages/agency/AgentBridgePage"));
const AgencyEmailsLogPage = React.lazy(() => import("@/pages/agency/AgencyEmailsLogPage"));
const AgencyWeeklyReportsPage = React.lazy(() => import("@/pages/agency/AgencyWeeklyReportsPage"));
const AuditLogPage = React.lazy(() => import("@/pages/agency/AuditLogPage"));
const TotpSetupPage = React.lazy(() => import("@/pages/agency/TotpSetupPage"));
const PipelinePage = React.lazy(() => import("@/pages/agency/PipelinePage"));
const AgencyHubDashboardPage = React.lazy(() => import("@/pages/agency/AgencyHubDashboardPage"));
const AgencyBillingPage = React.lazy(() => import("@/pages/agency/AgencyBillingPage"));
const AgencyClientDetailPage = React.lazy(() => import("@/pages/agency/AgencyClientDetailPage"));
const AgencyNewSiteWizardPage = React.lazy(() => import("@/pages/agency/AgencyNewSiteWizardPage"));
const AgencyTemplateLibraryPage = React.lazy(() => import("@/pages/agency/AgencyTemplateLibraryPage"));
const AgencyLaunchesPage = React.lazy(() => import("@/pages/agency/AgencyLaunchesPage"));
const AgencyLaunchDetailPage = React.lazy(() => import("@/pages/agency/AgencyLaunchDetailPage"));
const AgencyCloneSitePage = React.lazy(() => import("@/pages/agency/AgencyCloneSitePage"));
const PodSsoConsumePage = React.lazy(() => import("@/pages/admin/PodSsoConsumePage"));
const AgencyDashboardAdmin = React.lazy(() => import("@/pages/admin/AgencyAccessAdmin").then(m => ({ default: m.AgencyDashboardAdmin })));
const AgencyPricingAdmin = React.lazy(() => import("@/pages/admin/AgencyAccessAdmin").then(m => ({ default: m.AgencyPricingAdmin })));
const AgencyWidgetCatalogAdmin = React.lazy(() => import("@/pages/admin/AgencyAccessAdmin").then(m => ({ default: m.AgencyWidgetCatalogAdmin })));
const AgencyCGVAdmin = React.lazy(() => import("@/pages/admin/AgencyAccessAdmin").then(m => ({ default: m.AgencyCGVAdmin })));
const AgencyClientsAdmin = React.lazy(() => import("@/pages/admin/AgencyAccessAdmin").then(m => ({ default: m.AgencyClientsAdmin })));
const AgencyOnboardingRequestsAdmin = React.lazy(() => import("@/pages/admin/AgencyAccessAdmin").then(m => ({ default: m.AgencyOnboardingRequestsAdmin })));
const SportEffectifsAdmin = React.lazy(() => import("@/pages/admin/SportEffectifsAdmin"));
const SportSalesAdmin = React.lazy(() => import("@/pages/admin/SportSalesAdmin"));
const SportMarketingAdmin = React.lazy(() => import("@/pages/admin/SportMarketingAdmin"));
const SportModerationAdmin = React.lazy(() => import("@/pages/admin/SportModerationAdmin"));
const SportClubAdmin = React.lazy(() => import("@/pages/admin/SportClubAdmin"));
const CustomersAdmin = React.lazy(() => import("@/pages/admin/CustomersAdmin"));
const EmbedsAdmin = React.lazy(() => import("@/pages/admin/EmbedsAdmin"));
// === END LAZY ===

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
      <SettingsProvider>
        <UniverseProvider>
        <CustomerAuthProvider>
        <CartProvider>
          <ScrollToTop/>
          <Suspense fallback={
            <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#475569", fontFamily: "system-ui, sans-serif" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ width: 32, height: 32, border: "3px solid #e2e8f0", borderTopColor: "#6B2D5C", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }}/>
                <div style={{ fontSize: 13 }}>Chargement…</div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            </div>
          }>
          <Routes>
            {/* RACINE — Redirige vers le site IPH (/sport) puisque ce pod est dédié à Istres Provence Handball */}
            <Route path="/" element={<Navigate to="/sport" replace/>}/>
            <Route path="/site" element={<Navigate to="/sport" replace/>}/>
            <Route path="/realisations-cockpit" element={<CockpitPortfolioPage/>}/>
            <Route path="/cockpit-features" element={<CockpitFeaturesPage/>}/>
            <Route path="/brand-preview" element={<BrandPreviewPage/>}/>

            {/* SPORT CLUB (Phase 33o) — pages publiques portail club */}
            <Route path="/sport" element={<SportPublicLayout><SportHomePage/></SportPublicLayout>}/>
            <Route path="/sport/effectif" element={<FeatureGate moduleKey="sport_players_roster_enabled" moduleName="L'effectif joueurs"><SportPublicLayout><SportPlayersPage/></SportPublicLayout></FeatureGate>}/>
            <Route path="/sport/joueur/:slug" element={<FeatureGate moduleKey="sport_players_roster_enabled" moduleName="Les fiches joueurs"><SportPublicLayout><SportPlayerDetail/></SportPublicLayout></FeatureGate>}/>
            <Route path="/sport/mon-espace" element={<SportPublicLayout><SportMonEspace/></SportPublicLayout>}/>
            <Route path="/sport/calendrier" element={<SportPublicLayout><SportCalendarPage/></SportPublicLayout>}/>
            <Route path="/sport/classement" element={<SportPublicLayout><SportClassementPage/></SportPublicLayout>}/>
            <Route path="/sport/boutique" element={<FeatureGate moduleKey="shop_enabled" moduleName="La boutique en ligne"><SportPublicLayout><SportBoutique/></SportPublicLayout></FeatureGate>}/>
            <Route path="/sport/checkout" element={<FeatureGate moduleKey="shop_enabled" moduleName="Le panier"><SportPublicLayout><SportCheckout/></SportPublicLayout></FeatureGate>}/>
            <Route path="/sport/coach/planning" element={<FeatureGate moduleKey="sport_communication_enabled" moduleName="L'espace coach"><CoachPlanningPage/></FeatureGate>}/>
            <Route path="/sport/medias" element={<FeatureGate moduleKey="sport_media_gallery_enabled" moduleName="Les galeries médias"><SportPublicLayout><SportMedias/></SportPublicLayout></FeatureGate>}/>
            <Route path="/sport/actualites" element={<FeatureGate moduleKey="sport_blog_news_enabled" moduleName="Les actualités du club"><SportPublicLayout><SportActualites/></SportPublicLayout></FeatureGate>}/>
            <Route path="/sport/actualites/:slug" element={<FeatureGate moduleKey="sport_blog_news_enabled" moduleName="Les actualités"><SportPublicLayout><SportArticleDetail/></SportPublicLayout></FeatureGate>}/>
            <Route path="/sport/partenaires" element={<FeatureGate moduleKey="sport_partners_directory_enabled" moduleName="L'annuaire partenaires"><SportPublicLayout><SportPartenaires/></SportPublicLayout></FeatureGate>}/>
            <Route path="/sport/supporters" element={<FeatureGate moduleKey="sport_supporters_enabled" moduleName="L'espace supporters"><SportPublicLayout><SportSupporters/></SportPublicLayout></FeatureGate>}/>
            <Route path="/sport/rejoindre" element={<SportPublicLayout><SportRejoindre/></SportPublicLayout>}/>
            <Route path="/sport/club/histoire" element={<SportPublicLayout><SportHistoire/></SportPublicLayout>}/>
            <Route path="/sport/club/:slug" element={<SportPublicLayout><SportClubInnerPage/></SportPublicLayout>}/>
            <Route path="/sport/contact" element={<SportPublicLayout><SportClubInnerPage/></SportPublicLayout>}/>
            <Route path="/sport/legal/:kind" element={<SportPublicLayout><SportLegalPage/></SportPublicLayout>}/>

            {/* PERFS WALL — diffusable QR code (portrait, sans layout) */}
            <Route path="/sport/perfs-wall" element={<SportPerfsWall/>}/>
            {/* WIDGETS EMBEDDABLES — pour intégration WordPress / sites tiers */}
            <Route path="/embed" element={<EmbedWidgets/>}/>
            <Route path="/embed/:widget" element={<EmbedWidgets/>}/>
            {/* DASHBOARD OPÉRATEUR — saisie temps réel */}
            <Route path="/sport/operator" element={<SportOperator/>}/>
            {/* CONSOLE SPEAKER — tablette du speaker stade */}
            <Route path="/sport/speaker" element={<SportSpeaker/>}/>
            {/* GESTION DES JEUX — MVP, Pronostic, Lots */}
            <Route path="/sport/games-admin" element={<SportGamesAdmin/>}/>
            {/* PAGE PRODUIT BOUTIQUE — full-page (remplace la modal) */}
            <Route path="/sport/boutique/produit/:id" element={<FeatureGate moduleKey="shop_enabled" moduleName="La boutique"><SportPublicLayout><SportProduct/></SportPublicLayout></FeatureGate>}/>

            {/* PUBLIC */}
            <Route element={<PublicLayout/>}>
              <Route path="/site-template" element={<HomePage/>}/>
              <Route path="/services" element={<ServicesListPage/>}/>
              <Route path="/services/:slug" element={<ServiceDetailPage/>}/>
              <Route path="/realisations" element={<RealisationsListPage/>}/>
              <Route path="/realisations/categorie/:slug" element={<RealisationCategoryPage/>}/>
              <Route path="/realisations/:slug" element={<RealisationDetailPage/>}/>
              <Route path="/blog" element={<BlogListPage/>}/>
              <Route path="/blog/categorie/:slug" element={<BlogCategoryPage/>}/>
              <Route path="/blog/:slug" element={<BlogDetailPage/>}/>
              <Route path="/zones/:slug" element={<ZonePage/>}/>
              <Route path="/intervention/:slug" element={<LocalPage/>}/>
              <Route path="/contact" element={<ContactPage/>}/>
              <Route path="/equipe" element={<TeamPage/>}/>
              <Route path="/devis" element={<DynamicQuoteFormPage/>}/>
              <Route path="/devis-client/:token" element={<QuotePublicPage/>}/>
              <Route path="/devis-simple" element={<DevisPage/>}/>
              <Route path="/shop" element={<ShopPage/>}/>
              <Route path="/shop/:slug" element={<ProductPage/>}/>
              <Route path="/reservation" element={<ReservationPage/>}/>
              <Route path="/booking/confirmation" element={<BookingConfirmationPage/>}/>
              <Route path="/rentals" element={<RentalsListPage/>}/>
              <Route path="/rentals/:slug" element={<RentalDetailPage/>}/>
              <Route path="/rental/confirmation" element={<RentalConfirmationPage/>}/>
              <Route path="/account/login" element={<CustomerLoginPage/>}/>
              <Route path="/account/register" element={<CustomerRegisterPage/>}/>
              <Route path="/account" element={<AccountLayout/>}>
                <Route index element={<AccountDashboard/>}/>
                <Route path="orders" element={<AccountOrders/>}/>
                <Route path="orders/:oid" element={<AccountOrderDetail/>}/>
                <Route path="wallet" element={<AccountWallet/>}/>
                <Route path="favorites" element={<AccountFavorites/>}/>
                <Route path="addresses" element={<AccountAddresses/>}/>
                <Route path="profile" element={<AccountProfile/>}/>
              </Route>
              <Route path="/account/order-confirmation" element={<OrderConfirmationPage/>}/>
              <Route path="/mentions-legales" element={<LegalPage kind="mentions"/>}/>
              <Route path="/cgv" element={<LegalPage kind="cgv"/>}/>
              <Route path="/livraison-retours" element={<DeliveryReturnsPage/>}/>
              <Route path="/confidentialite" element={<LegalPage kind="confidentialite"/>}/>
            </Route>

            {/* Public onboarding — accessible directement sans wrapper (dark theme) */}
            <Route path="/onboarding" element={<OnboardingRequestPage/>}/>
            <Route path="/onboarding/confirmation" element={<OnboardingConfirmationPage/>}/>

            {/* IPH (Istres Handball) custom onboarding — invites visitors to launch their own site */}
            <Route path="/lancez-votre-site" element={<IphOnboardingPage/>}/>

            {/* Public validation client — Phase 24 */}
            <Route path="/valider/:slug" element={<PublicValidatePage/>}/>

            {/* SHORT LINK — public redirect */}
            <Route path="/l/:slug" element={<ShortLinkRedirect/>}/>

            {/* ESPACE CLIENT WECOCKPIT (HUB-side) — Phase 33m M1 */}
            <Route path="/mon-espace/login" element={<CustomerLoginPageHub/>}/>
            <Route path="/mon-espace/set-password" element={<CustomerSetPasswordPageHub/>}/>
            <Route path="/mon-espace/magic" element={<CustomerMagicLoginPageHub/>}/>
            <Route path="/mon-espace" element={<CustomerLayoutHub/>}>
              <Route index element={<CustomerDashboardPageHub/>}/>
              <Route path="site/:site_id" element={<CustomerSiteDetailsPageHub/>}/>
              <Route path="devis" element={<CustomerQuotesListPageHub/>}/>
              <Route path="devis/nouveau" element={<CustomerQuoteNewPageHub/>}/>
              <Route path="devis/:id" element={<CustomerQuoteDetailPageHub/>}/>
              <Route path="projets" element={<CustomerProjectsPageHub/>}/>
              <Route path="projets/:id" element={<CustomerProjectDetailPageHub/>}/>
              <Route path="contacts" element={<CustomerContactsPageHub/>}/>
            </Route>

            {/* ADMIN */}
            <Route path="/admin/login" element={<LoginPage/>}/>
            <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage/>}/>
            <Route path="/admin/reset-password" element={<AdminResetPasswordPage/>}/>
            <Route path="/admin/sso" element={<PodSsoConsumePage/>}/>
            <Route path="/admin/sso-client" element={<PodSsoClientConsumePage/>}/>
            <Route path="/admin" element={<AdminLayout/>}>
              <Route index element={<Dashboard/>}/>
              <Route path="dashboard" element={<Dashboard/>}/>
              <Route path="services" element={<ServicesAdmin/>}/>
              <Route path="realisations" element={<RealisationsAdmin/>}/>
              <Route path="quote-forms" element={<QuoteFormsAdmin/>}/>
              <Route path="quote-requests" element={<QuoteRequestsAdmin/>}/>
              <Route path="quotes/:id" element={<QuoteEditorPage/>}/>
              <Route path="blog" element={<BlogAdmin/>}/>
              <Route path="faq" element={<FAQAdmin/>}/>
              <Route path="zones" element={<ZonesAdmin/>}/>
              <Route path="leads" element={<LeadsAdmin/>}/>
              <Route path="sport-effectifs" element={<SportEffectifsAdmin/>}/>
              <Route path="sport-sales" element={<SportSalesAdmin/>}/>
              <Route path="sport-marketing" element={<SportMarketingAdmin/>}/>
              <Route path="sport-moderation" element={<SportModerationAdmin/>}/>
              <Route path="analytics" element={<AnalyticsAdmin/>}/>
              <Route path="rank-tracker" element={<RankTrackerAdmin/>}/>
              <Route path="local-seo" element={<LocalSEOAdmin/>}/>
              <Route path="media" element={<MediaAdmin/>}/>
              <Route path="short-links" element={<ShortLinksAdmin/>}/>
              <Route path="themes" element={<ThemesAdmin/>}/>
              <Route path="products" element={<ProductsAdmin/>}/>
              <Route path="shop-dashboard" element={<ShopDashboard/>}/>
              <Route path="pickup" element={<PickupAdmin/>}/>
              <Route path="orders" element={<OrdersAdmin/>}/>
              <Route path="qr-labels" element={<QrLabelsAdmin/>}/>
              <Route path="team" element={<TeamAdmin/>}/>
              <Route path="bookings" element={<BookingsAdmin/>}/>
              <Route path="bookings-config" element={<BookingsConfigAdmin/>}/>
              <Route path="rentals" element={<RentalsAdmin/>}/>
              <Route path="rentals-calendar" element={<RentalsCalendarAdmin/>}/>
              <Route path="rentals-dashboard" element={<RentalsDashboardAdmin/>}/>
              <Route path="sector-presets" element={<SectorPresetsAdmin/>}/>
              <Route path="agency-sites" element={<AgencySitesDashboard/>}/>
              <Route path="agency-alerts" element={<AgencyAlertsPage/>}/>
              <Route path="widgets" element={<WidgetsConsoleAdmin/>}/>
              <Route path="sport-club" element={<SportClubAdmin/>}/>
              <Route path="customers" element={<CustomersAdmin/>}/>
              <Route path="embeds" element={<EmbedsAdmin/>}/>
              <Route path="technical" element={<TechnicalAdmin/>}/>
              <Route path="test-mode" element={<TestModeAdmin/>}/>
              <Route path="categories" element={<CategoriesAdmin/>}/>
              <Route path="customer-groups" element={<CustomerGroupsAdmin/>}/>
              <Route path="license" element={<LicenseAdmin/>}/>
              <Route path="universes" element={<UniversesAdmin/>}/>
              <Route path="staff" element={<StaffAdmin/>}/>
              <Route path="legal" element={<LegalConfigAdmin/>}/>
              <Route path="legal-docs" element={<LegalDocsAdmin/>}/>
              <Route path="billing" element={<BillingAdmin/>}/>
              <Route path="shipping" element={<ShippingAdmin/>}/>
              <Route path="business-rules" element={<BusinessRulesAdmin/>}/>
              <Route path="rules/approval" element={<BusinessRulesAdmin section="approval"/>}/>
              <Route path="rules/b2b" element={<BusinessRulesAdmin section="b2b"/>}/>
              <Route path="rules/loyalty" element={<BusinessRulesAdmin section="loyalty"/>}/>
              <Route path="emails" element={<EmailsAdmin/>}/>
              <Route path="popup-callback" element={<PopupCallbackAdmin/>}/>
              <Route path="wishlist" element={<WishlistAdmin/>}/>
              <Route path="payments" element={<PaymentsAdmin/>}/>
              <Route path="onboarding" element={<OnboardingPage/>}/>
              <Route path="cockpit" element={<CockpitAdmin/>}/>
              <Route path="studio" element={<StudioAdmin/>}/>
              <Route path="sections" element={<HomeSectionsToggleAdmin/>}/>
              <Route path="import-products" element={<ProductsImporterAdmin/>}/>
              <Route path="agency-pairing" element={<AgencyPairingPage/>}/>
              <Route path="security" element={<SecurityAdmin/>}/>
              <Route path="header" element={<HeaderAdmin/>}/>
              <Route path="settings" element={<SettingsAdmin/>}/>
              <Route path="identity" element={<IdentityAdmin/>}/>
              <Route path="roles" element={<RolesAdmin/>}/>
              <Route path="sport-teams" element={<AmateurTeamsAdmin/>}/>
              <Route path="sport-articles" element={<SportArticlesAdmin/>}/>
              <Route path="sport-products" element={<SportProductsAdmin/>}/>
              <Route path="sport-pages" element={<SportPagesAdmin/>}/>
              <Route path="support" element={<SupportTicketsAdmin/>}/>
              <Route path="pos" element={<PosAdmin/>}/>
              <Route path="pos/station/:slug" element={<PosCashier/>}/>
              <Route path="pos/sessions" element={<PosSessions/>}/>
              <Route path="pos/z/:id" element={<PosZView/>}/>
            </Route>

            {/* ACCÈS AGENCE — layout dédié (dark cockpit), gated by is_hub + role=admin */}
            <Route path="/agency/login" element={<AgencyLoginPage/>}/>
            <Route path="/agency" element={<AgencyLayout/>}>
              <Route index element={<AgencyHubDashboardPage/>}/>
              <Route path="dashboard" element={<AgencyHubDashboardPage/>}/>
              <Route path="sites" element={<AgencySitesDashboard/>}/>
              <Route path="alerts" element={<AgencyAlertsPage/>}/>
              <Route path="clients" element={<AgencyClientsAdmin/>}/>
              <Route path="hub-customers" element={<HubCustomersAdmin/>}/>
              <Route path="projects" element={<ProjectsKanbanAdmin/>}/>
              <Route path="wizard/new-site" element={<AgencyNewSiteWizardPage/>}/>
              <Route path="clone-this-site" element={<AgencyCloneSitePage/>}/>
              <Route path="template-library" element={<AgencyTemplateLibraryPage/>}/>
              <Route path="launches" element={<AgencyLaunchesPage/>}/>
              <Route path="launches/:sid" element={<AgencyLaunchDetailPage/>}/>
              <Route path="clients/:id" element={<AgencyClientDetailPage/>}/>
              <Route path="onboarding" element={<AgencyOnboardingRequestsAdmin/>}/>
              <Route path="pricing" element={<AgencyPricingAdmin/>}/>
              <Route path="widgets" element={<AgencyWidgetCatalogAdmin/>}/>
              <Route path="cgv" element={<AgencyCGVAdmin/>}/>
              <Route path="studio" element={<StudioAdmin/>}/>
              <Route path="theme-generator" element={<ThemeGeneratorAdvanced/>}/>
              <Route path="content-scraper" element={<ContentScraperPage/>}/>
              <Route path="drafts" element={<OnboardingDraftsPage/>}/>
              <Route path="cockpit" element={<AgencyCockpitAdmin/>}/>
              <Route path="promo-codes" element={<PromoCodesPage/>}/>
              <Route path="domains" element={<DomainsPage/>}/>
              <Route path="cockpit-update" element={<CockpitUpdatePage/>}/>
              <Route path="pods" element={<PodsVaultPage/>}/>
              <Route path="pods/new" element={<PodPairingWizardPage/>}/>
              <Route path="agent-bridge" element={<AgentBridgePage/>}/>
              <Route path="emails-log" element={<AgencyEmailsLogPage/>}/>
              <Route path="weekly-reports" element={<AgencyWeeklyReportsPage/>}/>
              <Route path="audit-log" element={<AuditLogPage/>}/>
              <Route path="totp" element={<TotpSetupPage/>}/>
              <Route path="pipeline" element={<PipelinePage/>}/>
              <Route path="billing" element={<AgencyBillingPage/>}/>
              <Route path="presets" element={<SectorPresetsAdmin/>}/>
              <Route path="settings" element={<AgencySettingsPage/>}/>
            </Route>
          </Routes>
          </Suspense>
          <Toaster position="top-right" richColors/>
        </CartProvider>
        </CustomerAuthProvider>
        </UniverseProvider>
      </SettingsProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
